import { useEffect, useState } from 'react';

const DEFAULT_COOLDOWN_SECONDS = 60;

export interface ResendCooldown {
  /** Seconds remaining, or null when the action is available. */
  countdown: number | null;
  /** True while counting down (action should be disabled). */
  isCoolingDown: boolean;
  /** (Re)start the cooldown timer. */
  start: (seconds?: number) => void;
  /** Clear the cooldown immediately (e.g. after a failed send). */
  reset: () => void;
}

/**
 * Resend cooldown timer shared by the auth-code forms: ticks a countdown down
 * to zero, then clears itself. Replaces the duplicated `countdown` state +
 * `setTimeout` effect that lived in each verification form.
 */
export function useResendCooldown(
  initial: number | null = DEFAULT_COOLDOWN_SECONDS,
): ResendCooldown {
  const [countdown, setCountdown] = useState<number | null>(initial);

  useEffect(() => {
    if (countdown && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (countdown === 0) setCountdown(null);
  }, [countdown]);

  return {
    countdown,
    isCoolingDown: !!countdown,
    start: (seconds = DEFAULT_COOLDOWN_SECONDS) => setCountdown(seconds),
    reset: () => setCountdown(null),
  };
}
